import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// features
import { saveImportListSuccess } from '../sockets/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature new-import
import * as A from './actions';
import * as C from './constants';
//////////////////////////////////////////////////

const initialState = {
  errors: [],
  emptyRow: {},
  importList: [],
  importId: null,
  rolesGuids: null,
  importType: null,
  failedFields: [],
  sourceType: null,
  globalErrors: null,
  contractGuid: null,
  additionalFields: [],
  importFromFileData: [],
  cleanExistentRates: true,
  cleanExistentTrailers: false,
  trailerActivityListGuid: null,
  replaceExistentCompensations: false,
  mappers: {
    [C.IMPORT_TYPE_CLO]: {},
  },
  configOptions: {
    [C.IMPORT_TYPE_CLO]: {},
    [C.IMPORT_TYPE_TRUCK]: {},
    [C.IMPORT_TYPE_DRIVER]: {},
    [C.IMPORT_TYPE_BRANCH]: {},
    [C.IMPORT_TYPE_CARRIER]: {},
    [C.IMPORT_TYPE_TRAILER]: {},
    [C.IMPORT_TYPE_LOCATION]: {},
    [C.IMPORT_TYPE_GEO_FENCING_ZONE]: {},
    [C.IMPORT_TYPE_CARRIER_INSURANCE]: {},
  },
};

const setErrors = (state, data) => P.$set('errors', data, state);

const setInitialState = ({ mappers }) => R.assoc('mappers', mappers, initialState);

const toggleCleanExistentRates = (state) => P.$toggle('cleanExistentRates', state);

const setImportId = (state, importId) => P.$set('importId', importId, state);

const setGlobalErrors = (state, data) => P.$set('globalErrors', data, state);

const toggleCleanExistentTrailers = (state) => P.$toggle('cleanExistentTrailers', state);

const setSourceType = (state, sourceType) => P.$set('sourceType', sourceType, state);

const setAdditionalFields = (state, data) => P.$set('additionalFields', data, state);

const setRolesGuids = (state, rolesGuids) => P.$set('rolesGuids', rolesGuids, state);

const getAvailableImportMappersSuccess = (state, data) => P.$set('mappers', data, state);

const setContractGuid = (state, contractGuid) => P.$set('contractGuid', contractGuid, state);

const getImportFromFileDataSuccess = (state, data) => P.$set('importFromFileData', data, state);

const setTrailerActivityListGuid = (state, guid) => P.$set('trailerActivityListGuid', guid, state);

const getImportTemplateSuccess = (state, { data, type }) => {
  const emptyRow = R.compose(
    R.indexBy(R.prop(GC.FIELD_NAME)),
    R.map(({ type, name, index, required, fieldName, dropDownName }) => ({
      name,
      type,
      required,
      fieldName,
      dropDownName,
      readOnly: false,
      index: R.inc(index),
      [GC.FIELD_VALUE]: null,
      [GC.FIELD_LABEL]: null,
    })),
  )(R.or(data, []));

  return P.$all(
    P.$set('importId', null),
    P.$set('importList', []),
    P.$set('importType', type),
    P.$set('emptyRow', emptyRow),
    state,
  );
};

const addTableRows = (state, count) => {
  const { emptyRow, importList } = state;

  const rows = R.repeat(emptyRow, count);

  if (G.isNilOrEmpty(importList)) return P.$set('importList', rows, state);

  const newImportList = R.concat(importList, rows);

  return P.$set('importList', newImportList, state);
};

const changeImportList = (state, data) => (
  P.$set('importList', data, state)
);

const removeSelectedRows = (state, data) => {
  const { importList} = state;

  if (G.isNilOrEmpty(data)) return P.$set('importList', R.dropLast(1, importList), state);

  const reduceIndexed = R.addIndex(R.reduce);

  const newList = R.compose(
    R.values,
    R.omit(data),
    reduceIndexed((acc, curr, index) => R.assoc(index, curr, acc), {}),
  )(importList);

  return P.$set('importList', newList, state);
};

const saveImportList = (state, { guid, results, globalErrors }) => {
  const { emptyRow, importId, importType, importList, additionalFields, importFromFileData } = state;

  let fullImportList = R.or(importList, []);
  const lastElement = R.inc(R.length(R.values(emptyRow)));
  const rowWithAdditionalFields = R.compose(
    R.mergeRight(emptyRow),
    R.indexBy(R.prop(GC.FIELD_NAME)),
    G.mapIndexed((fieldName, index) => ({
      fieldName,
      [GC.FIELD_VALUE]: null,
      [GC.FIELD_LABEL]: null,
      [GC.FIELD_NAME]: fieldName,
      index: R.add(index, lastElement),
    })),
  )(R.or(additionalFields, []));

  if (G.isNotNilAndNotEmpty(importFromFileData)) {
    const mappedImportData = R.map((row) => {
      const values = R.compose(
        R.map((item) => {
          const { name } = item;

          const value = R.pathOr(null, [name], row);

          return R.mergeRight(item, { value, [GC.FIELD_LABEL]: value });
        }),
        R.pick(R.keys(row)),
      )(rowWithAdditionalFields);

      return R.mergeRight(rowWithAdditionalFields, values);
    }, importFromFileData);

    if (R.equals(importType, C.IMPORT_TYPE_CLO)) {
      fullImportList = mappedImportData;
    } else {
      fullImportList = R.concat(importList, mappedImportData);
    }
  }

  if (G.isNilOrEmpty(fullImportList)) return state;

  if (G.notEquals(guid, importId)) return state;

  const failedRows = R.compose(
    R.indexBy(R.prop('index')),
    R.map((row) => {
      const invalidFields = R.compose(
        R.indexBy(R.prop('fieldName')),
        R.pathOr([], ['error', 'invalidFields']),
      )(row);

      return R.assoc('invalidFields', invalidFields, row);
    }),
    R.filter(({ importStatus }) => G.notContain(importStatus, ['UPDATED', 'CREATED'])),
  )(R.or(results, []));
  const newList = R.compose(
    G.filterIndexed((_, index) => R.has(index, failedRows)),
    G.mapIndexed((row, index) => {
      const failedCells = R.compose(
        R.map(({ message, fieldName }) => {
          const failedCell = R.pathOr({}, [fieldName], row);

          return R.mergeRight(failedCell, { error: message, className: 'error' });
        }),
        R.pathOr([], [index, 'invalidFields']),
      )(failedRows);

      if (G.isNilOrEmpty(failedCells)) return row;

      return R.mergeRight(row, failedCells);
    }),
  )(fullImportList);
  const errors = R.compose(
    R.map((item) => ({
      index: item.index,
      errorMessage: R.path(['error', GC.FIELD_MESSAGE], item),
    })),
    R.filter(({ importStatus }) => R.equals(importStatus, 'ERROR')),
  )(R.or(results, []));

  return P.$all(
    P.$set('errors', errors),
    P.$set('importList', newList),
    P.$set('globalErrors', globalErrors),
    P.$set('emptyRow', rowWithAdditionalFields),
    state,
  );
};

const changeTableCell = (state, { path, cell }) => P.$set(path, cell, state);

const getBranchConfigsSuccess = (state, { type, dropdowns }) => {
  const dropdownsOptions = R.compose(
    R.map(({ options }) => R.map(({ storedValue, displayedValue }) => ({
      [GC.FIELD_VALUE]: storedValue,
      [GC.FIELD_LABEL]: displayedValue,
    }), options)),
    R.indexBy(R.prop(GC.FIELD_NAME)),
  )(dropdowns);

  return P.$set(`configOptions.${type}`, dropdownsOptions, state);
};

const addAdditionalFieldToTable = (state, { value, prefix }) => {
  const { emptyRow, importList } = state;

  const cellName = `${prefix}:${value}`;

  const additionalCell = {
    value: null,
    label: null,
    name: cellName,
    required: false,
    fieldName: cellName,
    className: 'success',
  };

  if (G.isNilOrEmpty(importList)) {
    return P.$set('emptyRow', { ...emptyRow, [cellName]: additionalCell }, state);
  }

  const newList = R.map((row) => ({ ...row, [cellName]: additionalCell }), importList);

  return P.$all(
    P.$set('importList', newList),
    P.$set('emptyRow', { ...emptyRow, [cellName]: additionalCell }),
    state,
  );
};

const toggleReplaceExistentCompensations = (state) => P.$toggle('replaceExistentCompensations', state);

export default createReducer({
  [A.setErrors]: setErrors,
  [A.setImportId]: setImportId,
  [A.addTableRows]: addTableRows,
  [A.setRolesGuids]: setRolesGuids,
  [A.setSourceType]: setSourceType,
  [A.setInitialState]: setInitialState,
  [A.changeTableCell]: changeTableCell,
  [A.setContractGuid]: setContractGuid,
  [A.setGlobalErrors]: setGlobalErrors,
  [A.changeImportList]: changeImportList,
  [saveImportListSuccess]: saveImportList,
  [A.removeSelectedRows]: removeSelectedRows,
  [A.setAdditionalFields]: setAdditionalFields,
  [A.getBranchConfigsSuccess]: getBranchConfigsSuccess,
  [A.getImportTemplateSuccess]: getImportTemplateSuccess,
  [A.toggleCleanExistentRates]: toggleCleanExistentRates,
  [A.addAdditionalFieldToTable]: addAdditionalFieldToTable,
  [A.setTrailerActivityListGuid]: setTrailerActivityListGuid,
  [A.toggleCleanExistentTrailers]: toggleCleanExistentTrailers,
  [A.getImportFromFileDataSuccess]: getImportFromFileDataSuccess,
  [A.getAvailableImportMappersSuccess]: getAvailableImportMappersSuccess,
  [A.toggleReplaceExistentCompensations]: toggleReplaceExistentCompensations,
}, initialState);
